import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const SITE_NAME = import.meta.env.VITE_SITE_NAME || 'Cripto News';
const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://criptonews.site';

export interface ToolFaq {
  question: string;
  answer: string;
}

interface ToolLayoutProps {
  /** Caminho canônico, ex.: /ferramentas/calculadora-dca */
  path: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  intro: string[];
  children: React.ReactNode;
  methodology?: string[];
  faqs?: ToolFaq[];
  /** Conteúdo extra (explicações longas) renderizado entre a ferramenta e a metodologia. */
  explanation?: React.ReactNode;
}

/**
 * Layout compartilhado das páginas de ferramenta: metadata única,
 * breadcrumbs, JSON-LD (WebApplication, BreadcrumbList e FAQPage quando há
 * FAQ real), introdução indexável, metodologia e aviso educativo.
 */
export const ToolLayout: React.FC<ToolLayoutProps> = ({
  path,
  title,
  metaTitle,
  metaDescription,
  intro,
  children,
  methodology,
  faqs,
  explanation,
}) => {
  const canonicalUrl = `${SITE_URL}${path}`;

  const jsonLd: any[] = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: `${title} | ${SITE_NAME}`,
      url: canonicalUrl,
      applicationCategory: 'FinanceApplication',
      operatingSystem: 'Web',
      inLanguage: 'pt-BR',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'BRL' },
      publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Início', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Ferramentas', item: `${SITE_URL}/ferramentas` },
        { '@type': 'ListItem', position: 3, name: title, item: canonicalUrl },
      ],
    },
  ];

  if (faqs && faqs.length > 0) {
    jsonLd.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: { '@type': 'Answer', text: faq.answer },
      })),
    });
  }

  return (
    <>
      <Helmet>
        <title>{metaTitle} | {SITE_NAME}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={`${metaTitle} | ${SITE_NAME}`} />
        <meta property="og:description" content={metaDescription} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          {/* Breadcrumb */}
          <nav className="text-sm text-gray-500 mb-4 flex items-center gap-2" aria-label="breadcrumb">
            <Link to="/" className="hover:text-blue-600 transition">Início</Link>
            <span>›</span>
            <Link to="/ferramentas" className="hover:text-blue-600 transition">Ferramentas</Link>
            <span>›</span>
            <span className="text-gray-700">{title}</span>
          </nav>

          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{title}</h1>

          {/* Introdução indexável */}
          <div className="mb-6 space-y-3">
            {intro.map((paragraph, index) => (
              <p key={index} className="text-gray-600 leading-relaxed">{paragraph}</p>
            ))}
          </div>

          {/* A ferramenta em si */}
          {children}

          {/* Explicação longa */}
          {explanation}

          {/* Metodologia */}
          {methodology && methodology.length > 0 && (
            <section className="bg-white rounded-xl shadow p-6 md:p-8 mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-3">Metodologia</h2>
              <ul className="space-y-2">
                {methodology.map((item, index) => (
                  <li key={index} className="flex items-start gap-2 text-gray-700 text-[15px] leading-relaxed">
                    <span className="flex-shrink-0 mt-2 w-1.5 h-1.5 rounded-full bg-blue-500" aria-hidden />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* FAQ */}
          {faqs && faqs.length > 0 && (
            <section className="bg-white rounded-xl shadow p-6 md:p-8 mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Perguntas frequentes</h2>
              <div className="space-y-5">
                {faqs.map((faq, index) => (
                  <div key={index}>
                    <h3 className="font-semibold text-gray-900 mb-1">{faq.question}</h3>
                    <p className="text-gray-600 text-[15px] leading-relaxed">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Aviso educativo */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-800 leading-relaxed">
            <strong>⚠️ Aviso:</strong> esta ferramenta tem finalidade exclusivamente educativa e informativa.
            Os resultados são estimativas baseadas em dados públicos e não constituem recomendação de
            investimento. Resultados passados não garantem resultados futuros. Criptoativos envolvem alto
            risco e volatilidade.
          </div>
        </div>
      </div>
    </>
  );
};
